import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (err) {
      // 401 here is the normal, expected state for an unauthenticated visitor.
      if (process.env.NODE_ENV !== "production" && typeof console.debug === "function") {
        console.debug("auth/me not authenticated", err?.response?.status);
      }
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const signup = async (email, password, name) => {
    const { data } = await api.post("/auth/signup", { email, password, name });
    if (data.session_token) localStorage.setItem("ss_token", data.session_token);
    setUser(await refresh());
    return data;
  };

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.session_token) localStorage.setItem("ss_token", data.session_token);
    setUser(await refresh());
    return data;
  };

  const googleExchange = async (session_id) => {
    const { data } = await api.post("/auth/google/session", { session_id });
    if (data.session_token) localStorage.setItem("ss_token", data.session_token);
    setUser(await refresh());
    return data;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("logout failed", err);
    }
    localStorage.removeItem("ss_token");
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, refresh, signup, login, logout, googleExchange, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
